import { Button, Logo } from '@centerpanos/ui';
import { useQuery } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { Field, Input } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

const SAVED_KEY = 'painel:credentials';

function getSaved(): { email: string } | null {
  try {
    const raw = localStorage.getItem(SAVED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { email?: string; password?: unknown };
    // Migração: remove senhas salvas em versões anteriores.
    if (parsed.password !== undefined) {
      const { password: _removed, ...safe } = parsed;
      localStorage.setItem(SAVED_KEY, JSON.stringify(safe));
    }
    return parsed.email ? { email: parsed.email } : null;
  } catch {
    return null;
  }
}

export function LoginPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();

  const { data: publicSettings } = useQuery({
    queryKey: ['settings', 'public'],
    queryFn: api.settings.getPublic,
    staleTime: Infinity,
  });

  const logoUrl = publicSettings?.logoPrincipal ?? null;
  const saved = getSaved();
  const [email, setEmail] = useState(saved?.email ?? '');
  const [password, setPassword] = useState('');
  const [lembrar, setLembrar] = useState(saved !== null);
  const [senhaVisivel, setSenhaVisivel] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await login(email, password);
      try {
        if (lembrar) {
          localStorage.setItem(SAVED_KEY, JSON.stringify({ email }));
        } else {
          localStorage.removeItem(SAVED_KEY);
        }
      } catch {
        // localStorage pode falhar em contexto privado; ignoramos silenciosamente.
      }
      navigate('/');
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Não foi possível entrar. Verifique se a API está no ar.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-brand-600 flex min-h-dvh items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex justify-center">
          <Logo variant="onDark" src={logoUrl ?? undefined} className="h-12" />
        </div>

        <form onSubmit={handleSubmit} className="rounded-card shadow-card space-y-4 bg-white p-6">
          <div>
            <h1 className="text-ink-900 text-lg font-bold">Painel administrativo</h1>
          </div>

          <Field label="E-mail" required>
            <Input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              required
              autoFocus={!saved}
            />
          </Field>

          <Field label="Senha" required>
            <div className="relative">
              <Input
                type={senhaVisivel ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
                className="pr-9"
                autoFocus={!!saved}
              />
              <button
                type="button"
                onClick={() => setSenhaVisivel((v) => !v)}
                className="text-ink-400 hover:text-ink-700 absolute top-1/2 right-3 -translate-y-1/2"
                aria-label={senhaVisivel ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {senhaVisivel ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
          </Field>

          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={lembrar}
              onChange={(e) => setLembrar(e.target.checked)}
              className="accent-brand-600 size-4"
            />
            <span className="text-ink-600">Lembrar senha</span>
          </label>

          {error ? (
            <p role="alert" className="rounded-button bg-price-50 text-price-600 px-3 py-2 text-sm">
              {error}
            </p>
          ) : null}

          <Button type="submit" variant="secondary" full disabled={submitting}>
            {submitting ? 'Entrando…' : 'Entrar'}
          </Button>
        </form>

        <p className="text-brand-200 mt-4 text-center text-xs">
          Center Panos Guarulhos — acesso restrito à equipe.
        </p>
      </div>
    </div>
  );
}
